// The contacts table's column catalog: which columns exist, which show by
// default, and how each sorts. Pure (no React) so it's unit-testable; the
// table turns these into Arco columns.
//
// Sorting happens on the server (the table pages in as you scroll), so each
// sortable column carries the backend sort key (see gormstore contactOrder).
// Custom fields sort as cf:<key>, or cfn:<key> for number fields.

export const DEFAULT_SORT = "-signed_up"; // newest signups first
export const DEFAULT_VISIBLE = ["name", "email", "status", "tags", "lists", "signed_up"];
const STORAGE_KEY = "jc.contacts.columns.v1";

// When the contact signed up: to the filtered list when there is one (that
// subscription's date, or when they were added if it's still pending),
// otherwise when they were first added. Mirrors the server's signed_up sort.
export function signedUpDate(contact, contactListFilterId) {
  const added = contact.added_email_date || contact.created_date || null;
  const onList = contactListFilterId && contactListFilterId !== "all" && contactListFilterId !== "None";
  if (onList) {
    const sub = (contact.subscriptions || []).find((s) => s.contact_list_id === contactListFilterId);
    if (sub && sub.subscribe_date) return sub.subscribe_date;
  }
  return added;
}

// Standard columns. `kind` tells the table how to render; `sort` is the
// backend key (absent = not sortable).
export const STANDARD_COLUMNS = [
  { key: "name", title: "Name", kind: "text", field: "name", sort: "name" },
  { key: "email", title: "Email", kind: "text", field: "email", sort: "email" },
  { key: "status", title: "Status", kind: "status", sort: "status" },
  { key: "tags", title: "Tags", kind: "tags" },
  { key: "lists", title: "Lists", kind: "lists" },
  { key: "signed_up", title: "Signed up", kind: "signed_up", sort: "signed_up" },
  { key: "added", title: "Added", kind: "date", field: "created_date", sort: "added" },
  { key: "confirmed", title: "Confirmed", kind: "date", field: "confirm_email_date", sort: "confirmed" },
  { key: "last_opened", title: "Last opened", kind: "date", field: "last_opened_date", sort: "last_opened" },
  { key: "mails_sent", title: "Emails sent", kind: "number", field: "mails_sent", sort: "mails_sent" },
  { key: "city", title: "City", kind: "text", field: "city", sort: "city" },
  { key: "state", title: "State", kind: "text", field: "state", sort: "state" },
  { key: "country", title: "Country", kind: "text", field: "country", sort: "country" },
];

// One column per active contact custom field, keyed cf:<key>.
export function customFieldColumns(defs) {
  return (defs || [])
    .filter((d) => !d.archived)
    .sort((a, b) => (a.position || 0) - (b.position || 0))
    .map((d) => ({
      key: "cf:" + d.key,
      title: d.label || d.key,
      kind: "custom",
      fieldType: d.field_type,
      cfKey: d.key,
      sort: (d.field_type === "number" ? "cfn:" : "cf:") + d.key,
    }));
}

export function allColumns(customFieldDefs) {
  return [...STANDARD_COLUMNS, ...customFieldColumns(customFieldDefs)];
}

// Splits a sort string into { key, desc } and back.
export function parseSort(sort) {
  const s = sort || DEFAULT_SORT;
  return s.startsWith("-") ? { key: s.slice(1), desc: true } : { key: s, desc: false };
}
export const formatSort = (key, desc) => (desc ? "-" : "") + key;

// Remembered per browser: which columns are showing, in catalog order.
export function loadVisibleColumns() {
  try {
    const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "null");
    if (Array.isArray(saved) && saved.length) return saved;
  } catch (e) {
    // storage blocked or corrupt: fall through to the default
  }
  return DEFAULT_VISIBLE;
}
export function saveVisibleColumns(keys) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(keys));
  } catch (e) {
    // storage blocked: the choice just won't persist
  }
}

// The visible columns, in catalog order, dropping keys that no longer exist
// (e.g. an archived custom field).
export function visibleColumns(keys, customFieldDefs) {
  const want = new Set(keys);
  return allColumns(customFieldDefs).filter((c) => want.has(c.key));
}
